import useMediaQuery from "@mui/material/useMediaQuery";

import type { HeaderChoicesByScope, HeaderScope } from "./patient-header-slice";

/**
 * Viewport heights at or below this collapse the patient header by default.
 *
 * The expanded header measures 281px at 1528px wide — it is content-driven and wraps taller
 * as the viewport narrows — which is 40% of the 698px viewport DEV-2533 was reported on.
 * 899 also covers the common 1366x768 and 1440x900 laptops (browser chrome leaves them
 * roughly 660-790px), so this is a deliberately broad net rather than a fix aimed only at
 * the one reported screen.
 */
export const SHORT_VIEWPORT_MAX_HEIGHT_PX = 899;

/**
 * Routes whose own content needs the full height, regardless of viewport or user choice.
 * Pre-existing behaviour, lifted out of `patient-header-layout` unchanged.
 */
export const isRouteRequiringCollapsedHeader = (pathname: string) => {
  const isAudiologyReport =
    /^\/patients\/\d+\/audiology\/\d+(\/)?(preview)?(\/)?$/.test(pathname);

  const isNewInvoice = /^\/patients\/\d+\/billing\/new(\/)?$/.test(pathname);

  return isAudiologyReport || isNewInvoice;
};

/**
 * The one route that starts collapsed on a short viewport.
 *
 * The route is declared `e-documents/*` (`patients.routes.tsx`), so folder deep-links have to
 * match too. Scoped to this page on purpose: the first version of DEV-2533 auto-collapsed on
 * every patient tab and was reverted for it (#6636). E-Documents is where the header actually
 * costs the user something — it is the tab whose content could not be scrolled to.
 */
export const isEDocumentsRoute = (pathname: string) =>
  /^\/patients\/\d+\/e-documents(\/.*)?$/.test(pathname);

/**
 * Which preference bucket this route's header state is remembered in.
 *
 * Two peer buckets, so a choice made on E-Documents and a choice made anywhere else in the chart
 * never overwrite each other. See `HeaderScope` in `patient-header-slice` for why the split exists.
 *
 * Safe to call with any pathname — the provider that uses it wraps the whole authenticated app,
 * and a non-patient route simply reads as `"other"`. It can only ever be reached from the two
 * patient-header affordances, so that case does not arise in practice.
 */
export const getHeaderScope = (pathname: string): HeaderScope =>
  isEDocumentsRoute(pathname) ? "e-documents" : "other";

export type HeaderStateInputs = {
  pathname: string;
  isShortViewport: boolean;
  storedChoices: HeaderChoicesByScope;
};

/**
 * Should the patient header be collapsed right now?
 *
 * Pure and exhaustive so the precedence is testable on its own — this used to be an
 * if/else inside an effect whose dependency array had to lie (`isHeaderCollapsed` was
 * omitted with an eslint-disable "to prevent resets") precisely because it could not tell a
 * deliberate choice from a stale one.
 *
 * Precedence, highest first:
 *   1. the route demands it — some pages cannot function with 281px of header
 *   2. the user's own last click ON THIS SCOPE, remembered across tabs, reloads and screen sizes.
 *      This is the whole point; the re-clicking on every tab change is what users complained
 *      about. Read per scope, so a choice about E-Documents never governs Chart Notes or the other
 *      way round.
 *   3. a tall viewport expands. This is the guarantee that a large screen NEVER collapses on its
 *      own — it is the only thing standing between us and #6623, which auto-collapsed on tabs
 *      nobody asked it to and was reverted wholesale (#6636). Note it sits BELOW step 2 on
 *      purpose: suppressing an automatic collapse is not the same as discarding a deliberate
 *      click, and conflating the two is why a large-screen user could collapse the header and
 *      watch the next navigation throw the choice away.
 *   4. otherwise collapsed only on E-Documents — the DEV-2533 case
 *
 * `storedChoices` MUST be the hydrated record, never the "still loading" placeholder. Acting on an
 * empty record while the preference is being read makes step 4 fire on a page that should have
 * been expanded, painting a header the user then watches disappear. See `patient-header-layout`,
 * which gates on `preferenceStatus`.
 */
export const resolveHeaderState = ({
  pathname,
  isShortViewport,
  storedChoices
}: HeaderStateInputs): { collapsed: boolean } => {
  if (isRouteRequiringCollapsedHeader(pathname)) return { collapsed: true };

  const scope = getHeaderScope(pathname);
  const storedChoice = storedChoices[scope];
  if (storedChoice) return { collapsed: storedChoice === "collapsed" };

  if (!isShortViewport) return { collapsed: false };
  return { collapsed: scope === "e-documents" };
};

/**
 * Is a click on this route worth remembering?
 *
 * No, on a route whose header state `isRouteRequiringCollapsedHeader` dictates. The choice can
 * never be honoured there — step 1 outranks it on this visit and every later one — so recording it
 * would do nothing except overwrite the preference the user set on a page where it DOES apply.
 * Opening an audiology report and clicking "Show patient details" must not silently un-collapse
 * Chart Notes.
 *
 * A rule rather than an inline guard in `persistHeaderChoice` so it is testable on its own, like
 * every other rule in this module; the provider has no test harness.
 */
export const shouldPersistHeaderChoice = (pathname: string) =>
  !isRouteRequiringCollapsedHeader(pathname);

/**
 * Viewport/motion inputs for `resolveHeaderState`.
 *
 * `noSsr: true` on both queries is load-bearing. Without it MUI returns the default `false`
 * on the first render and only corrects itself in an effect — so a short viewport would
 * paint a full 281px header and then yank it away, and a reduced-motion user would get the
 * very animation they asked not to see.
 */
export default function usePatientHeaderViewport() {
  const isShortViewport = useMediaQuery(
    `(max-height: ${SHORT_VIEWPORT_MAX_HEIGHT_PX}px)`,
    { noSsr: true }
  );

  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)",
    { noSsr: true }
  );

  return { isShortViewport, prefersReducedMotion };
}
